import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router";

import { ClientViewModeToggle, SelectionCardGroup } from "@/components/clients";
import { clientPath, useClientBasePath } from "@/hooks/use-client-base-path";
import { useClientViewMode } from "@/hooks/use-client-view-mode";
import { useTciSession } from "@/hooks/use-tci-session";
import { useVisitorTracking } from "@/hooks/use-visitor-tracking";
import {
  frictionTitleToWorkflowId,
  getWorkflow,
  isBusinessSector,
  listFrictionPoints,
} from "@/lib/tci";

/**
 * Friction picker for the selected business sector (Figma 563:857).
 * Presenter / Visitor toggle only on `/presenter`.
 */
export function ClientWorkflowPage() {
  const navigate = useNavigate();
  const basePath = useClientBasePath();
  const { session, startSession } = useTciSession();
  const { track } = useVisitorTracking();
  const [viewMode, setViewMode] = useClientViewMode();
  const showViewModeToggle = basePath === "/presenter";

  const businessSector = session?.businessSector ?? null;
  const sectorOk = Boolean(businessSector && isBusinessSector(businessSector));

  useEffect(() => {
    if (!sectorOk) return;
    track({ currentStep: "workflow", lastEvent: "view_workflow" });
  }, [sectorOk, track]);

  if (!sectorOk) {
    return <Navigate to={basePath} replace />;
  }

  const frictionOptions = listFrictionPoints(businessSector).flatMap(
    (point) => {
      const workflowId = frictionTitleToWorkflowId(point.title);
      if (!workflowId) return [];
      const workflow = getWorkflow(workflowId);
      return [
        {
          id: workflowId,
          title: point.title,
          description: workflow.description,
        },
      ];
    },
  );

  function handleSelectFriction(workflowId: string) {
    const matched = frictionOptions.find((option) => option.id === workflowId);
    if (!matched) return;
    startSession(matched.id);
    track({
      currentStep: "workflow",
      lastEvent: "workflow_selected",
      workflowId: matched.id,
      businessSector: businessSector ?? undefined,
    });
    navigate(
      `${clientPath(basePath, "calculator/1")}?workflow=${encodeURIComponent(matched.id)}`,
    );
  }

  return (
    <section className="my-auto flex w-full flex-col gap-10 sm:gap-14 lg:gap-20">
      <div className="flex w-full flex-col gap-6 sm:gap-10">
        {showViewModeToggle ? (
          <div className="flex w-full justify-end">
            <ClientViewModeToggle
              className="shrink-0"
              value={viewMode}
              onChange={setViewMode}
            />
          </div>
        ) : null}

        <h1 className="w-full max-w-[507px] font-['Inter'] text-[28px] font-normal leading-9 tracking-[-1.2px] text-white sm:text-4xl sm:leading-[44px] sm:tracking-[-1.8px] lg:text-[48.4px] lg:leading-[56.4px] lg:tracking-[-2.82px]">
          Find Out How Much Profit Is Trapped In Your{" "}
          <span className="text-[#38bdf8]">Daily Operations</span>
        </h1>
      </div>

      <div className="flex w-full flex-col items-start gap-4">
        <p className="font-['Inter'] text-base font-normal leading-9 tracking-[-1.2px] text-[#f5f3ee] sm:text-[20px]">
          Where is the most manual friction in your business right now?
        </p>

        <SelectionCardGroup
          aria-label="Friction type"
          className="w-full"
          options={frictionOptions}
          onChange={handleSelectFriction}
        />
      </div>
    </section>
  );
}
